import { APP_CHAIN } from '@/client/confs/app';
import { batchedRead, type MulticallClient } from '@/client/hooks/multicall-batch';
import { useAuth } from '@/client/hooks/use-auth';
import { useWallet } from '@/client/hooks/use-wallet';
import { publicClient as defaultPublicClient } from '@/client/utils/chain-client';
import { MarketStatus } from '@/libs/sdk/api';
import { focusManager, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Address, createPublicClient, custom } from 'viem';

// One read client per wallet transport. batchedRead groups reads by client
// object, so a client per hook sent the reads of one panel as separate multicalls.
const walletReadClients = new WeakMap<object, MulticallClient>();

/**
 * Returns a publicClient that uses the wallet's RPC provider when connected,
 * falling back to the default (server-configured) publicClient otherwise.
 * This distributes RPC load across users' own wallet providers (MetaMask, etc.)
 * instead of funneling all reads through our public RPC endpoints.
 * The wallet's provider answers for the chain the wallet is on, so it serves
 * reads only while the wallet is connected on APP_CHAIN.
 */
const usePublicClient = (): MulticallClient => {
  const { walletClient, isConnected } = useWallet();
  const transport = isConnected && walletClient?.chain?.id === APP_CHAIN.id ? walletClient.transport : undefined;
  return useMemo(() => {
    if (!transport) return defaultPublicClient as unknown as MulticallClient;
    let client = walletReadClients.get(transport);
    if (!client) {
      // wagmi's transport does not retry; no retries here either, react-query owns them.
      client = createPublicClient({
        chain: APP_CHAIN,
        transport: custom(transport, { retryCount: 0 }),
      }) as unknown as MulticallClient;
      walletReadClients.set(transport, client);
    }
    return client;
  }, [transport]);
};

// Minimal ABI fragments — only the functions we need

const erc20BalanceOfAbi = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

const erc1155BalanceOfAbi = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'id', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

const predictionPriceAbi = [
  {
    type: 'function',
    name: 'price',
    stateMutability: 'view',
    inputs: [{ name: 'option', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

const payoutDenominatorAbi = [
  {
    type: 'function',
    name: 'payoutDenominator',
    stateMutability: 'view',
    inputs: [{ name: 'conditionId', type: 'bytes32' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

// Query-option factories keep the read logic pure and testable (mirrors the
// buildXQueryOptions pattern already used in the codebase). Each queryFn routes
// its read through `batchedRead`, so all reads a market issues in one polling
// cycle collapse into a single multicall. refetchInterval/staleTime are the
// only cadence knobs.

// A signer's balance and positions change through the signer's own actions, and
// those refresh the reads at once (trade-success-queries.ts, SSE user events).
// The poll is a fallback.
const USER_READ_INTERVAL = 60_000;

type PositionQuery = {
  state: { status: string; data?: bigint; dataUpdateCount: number; errorUpdateCount: number };
};

// Successful zero reads in a row per position query. Each new result counts
// once, however often react-query evaluates refetchInterval.
const zeroReads = new WeakMap<object, { results: number; inRow: number }>();
const zeroReadsInRow = (query: PositionQuery) => {
  const { status, data, dataUpdateCount, errorUpdateCount } = query.state;
  const results = dataUpdateCount + errorUpdateCount;
  const run = zeroReads.get(query) ?? { results: 0, inRow: 0 };
  if (run.results !== results) {
    run.results = results;
    run.inRow = status === 'success' && data === 0n ? run.inRow + 1 : 0;
    zeroReads.set(query, run);
  }
  return run.inRow;
};

export const buildOnChainBalanceQuery = (
  client: MulticallClient,
  tokenAddress: Address | undefined,
  userAddress: Address | undefined
) => ({
  enabled: !!tokenAddress && !!userAddress,
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the RPC client is not data
  queryKey: ['OnChainBalance', tokenAddress, userAddress],
  queryFn: () =>
    batchedRead<bigint>(client, {
      address: tokenAddress!,
      abi: erc20BalanceOfAbi,
      functionName: 'balanceOf',
      args: [userAddress!],
    }),
  // The trade panel takes the balance from the header query (useSingerTokenBalance);
  // it uses this read only while that query has failed.
  refetchInterval: USER_READ_INTERVAL,
  staleTime: USER_READ_INTERVAL,
});

export const buildOnChainPositionQuery = (
  client: MulticallClient,
  ctfAddress: Address | undefined,
  userAddress: Address | undefined,
  positionId: bigint | undefined,
  marketStatus?: MarketStatus
) => ({
  enabled: !!ctfAddress && !!userAddress && positionId !== undefined,
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the RPC client is not data; positionId is keyed as a string
  queryKey: ['OnChainPosition', ctfAddress, userAddress, positionId?.toString()],
  queryFn: () =>
    batchedRead<bigint>(client, {
      address: ctfAddress!,
      abi: erc1155BalanceOfAbi,
      functionName: 'balanceOf',
      args: [userAddress!, positionId!],
    }),
  // A closed market takes no trades: two successful zero reads in a row mean the
  // claim burned the position (or the wallet never held it), and the poll stops.
  // One zero can come from a node that lags behind. A failed or pending read
  // keeps the poll. Only status closed (4) is terminal; unknown values keep it.
  refetchInterval: (query: PositionQuery) => {
    // Count every result, also the ones read before the market closed.
    const zeros = zeroReadsInRow(query);
    return marketStatus === MarketStatus.closed && zeros >= 2 ? false : USER_READ_INTERVAL;
  },
  staleTime: USER_READ_INTERVAL,
});

export const buildConditionResolvedQuery = (
  client: MulticallClient,
  ctfAddress: Address | undefined,
  conditionId: `0x${string}` | undefined
) => ({
  enabled: !!ctfAddress && !!conditionId,
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the RPC client is not data
  queryKey: ['ConditionResolved', ctfAddress, conditionId],
  queryFn: async () => {
    const denominator = await batchedRead<bigint>(client, {
      address: ctfAddress!,
      abi: payoutDenominatorAbi,
      functionName: 'payoutDenominator',
      args: [conditionId!],
    });
    return denominator > 0n;
  },
  // payoutDenominator never returns to 0, so a resolved condition stops polling.
  refetchInterval: (query: { state: { data?: boolean } }) => (query.state.data === true ? false : 15_000),
  staleTime: 15_000,
});

export const buildOnChainPriceQuery = (
  client: MulticallClient,
  marketAddress: Address | undefined,
  optionIndex: number | undefined
) => ({
  enabled: !!marketAddress && optionIndex !== undefined && optionIndex >= 0,
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the RPC client is not data
  queryKey: ['OnChainPrice', marketAddress, optionIndex],
  queryFn: () =>
    batchedRead<bigint>(client, {
      address: marketAddress!,
      abi: predictionPriceAbi,
      functionName: 'price',
      args: [BigInt(optionIndex!)],
    }),
  refetchInterval: 10_000,
  staleTime: 10_000,
});

/**
 * Read ERC-20 balanceOf from chain.
 * Returns the raw bigint balance, or undefined while loading / on error.
 */
export const useOnChainBalance = (tokenAddress: Address | undefined, userAddress: Address | undefined) => {
  const client = usePublicClient();
  return useQuery(buildOnChainBalanceQuery(client, tokenAddress, userAddress));
};

// The part of api.sse that a position read uses.
type UserEvent = 'user:position_changed' | 'user:asset_changed';
type UserEventSource = {
  addEventListener: (event: UserEvent, callback: (data?: unknown) => void) => unknown;
  removeEventListener: (event: UserEvent, callback: (data?: unknown) => void) => unknown;
};

type OnChainPositionOptions = {
  /** Stops the poll once a closed market's position reads 0 (claimed). */
  marketStatus?: MarketStatus;
  /** Re-reads the user's positions on the user's SSE position and asset events. */
  sse?: UserEventSource;
};

/**
 * Read ERC-1155 balanceOf(address, uint256) from chain.
 * Used for CTF position token balances.
 */
export const useOnChainPosition = (
  ctfAddress: Address | undefined,
  userAddress: Address | undefined,
  positionId: bigint | undefined,
  { marketStatus, sse }: OnChainPositionOptions = {}
) => {
  const client = usePublicClient();
  const queryClient = useQueryClient();
  const query = useQuery(buildOnChainPositionQuery(client, ctfAddress, userAddress, positionId, marketStatus));
  const enabled = query.isEnabled;
  const uid = useAuth().data?.uid;
  useEffect(() => {
    if (!sse || !enabled) return;
    // Any position of this user can have changed: re-read every mounted one and
    // join a read already in flight. An event for another uid is ignored. In a
    // hidden tab the reads are only marked stale; the focus refetch reads them.
    const refresh = (event?: unknown) => {
      const eventUid = (event as { uid?: unknown } | undefined)?.uid;
      // String(): the login response can carry the uid as a number.
      if (eventUid !== undefined && String(eventUid) !== String(uid)) return;
      void queryClient.invalidateQueries(
        {
          queryKey: ['OnChainPosition', ctfAddress, userAddress],
          refetchType: focusManager.isFocused() ? 'active' : 'none',
        },
        { cancelRefetch: false }
      );
    };
    sse.addEventListener('user:position_changed', refresh);
    sse.addEventListener('user:asset_changed', refresh);
    return () => {
      sse.removeEventListener('user:position_changed', refresh);
      sse.removeEventListener('user:asset_changed', refresh);
    };
  }, [sse, enabled, uid, queryClient, ctfAddress, userAddress]);
  return query;
};

/**
 * Check if a CTF condition has been resolved on-chain.
 * Returns true if payoutDenominator > 0 (condition resolved, positions redeemable).
 */
export const useConditionResolved = (ctfAddress: Address | undefined, conditionId: `0x${string}` | undefined) => {
  const client = usePublicClient();
  return useQuery(buildConditionResolvedQuery(client, ctfAddress, conditionId));
};

/**
 * Read PredictionCTF.price(uint256) from chain.
 * Returns the on-chain price for a given option index.
 */
export const useOnChainPrice = (marketAddress: Address | undefined, optionIndex: number | undefined) => {
  const client = usePublicClient();
  return useQuery(buildOnChainPriceQuery(client, marketAddress, optionIndex));
};
